import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Modal, Field, AdminTitle } from "@/pages/admin/AdminUI";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";

const empty = { name: "", category: "TikTok Ads", short_description: "", full_description: "", price: 0, delivery_time: "", features: [], packages: [], published: true, featured: false, order_button_text: "Order Now", seo_title: "", seo_description: "", sort_order: 0 };

export default function AdminServices() {
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/services").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); api.get("/service-categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s, features: s.features || [], packages: s.packages || [] }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, price: +form.price, sort_order: +form.sort_order };
      if (editing) await api.put(`/admin/services/${editing.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success("Service saved"); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
    setSaving(false);
  };
  const del = async (s) => { if (!window.confirm(`Delete "${s.name}"?`)) return; await api.delete(`/admin/services/${s.id}`); toast.success("Deleted"); load(); };

  const setFeatures = (v) => setForm({ ...form, features: v.split("\n").filter(Boolean) });
  const updatePkg = (i, k, v) => { const p = [...form.packages]; p[i] = { ...p[i], [k]: k === "price" ? +v : v }; setForm({ ...form, packages: p }); };

  if (items === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-400" size={36} /></div>;

  return (
    <div>
      <AdminTitle title="Services" action={<button onClick={openNew} className="gold-btn text-sm" data-testid="admin-add-service"><Plus size={16} /> Add Service</button>} />
      <div className="card-lux overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-services-table">
            <thead className="bg-black/30 text-slate-500 text-xs uppercase"><tr><th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Category</th><th className="text-left px-5 py-3">Price</th><th className="text-left px-5 py-3">Status</th><th className="text-right px-5 py-3">Actions</th></tr></thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t border-white/5" data-testid={`admin-service-row-${s.slug}`}>
                  <td className="px-5 py-3 text-white">{s.name} {s.featured && <span className="text-amber-400 text-xs">★</span>}</td>
                  <td className="px-5 py-3 text-slate-400">{s.category}</td>
                  <td className="px-5 py-3 text-white">{s.price} USDT</td>
                  <td className="px-5 py-3">{s.published ? <span className="text-emerald-400 text-xs">Published</span> : <span className="text-slate-500 text-xs">Draft</span>}</td>
                  <td className="px-5 py-3 text-right space-x-2">
                    <button onClick={() => openEdit(s)} className="text-slate-400 hover:text-amber-400" data-testid={`edit-service-${s.slug}`}><Pencil size={16} /></button>
                    <button onClick={() => del(s)} className="text-slate-400 hover:text-red-400" data-testid={`delete-service-${s.slug}`}><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Service" : "New Service"} wide>
        <form onSubmit={save} className="space-y-4" data-testid="service-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name"><input required className="input-lux" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-name-field" /></Field>
            <Field label="Category">
              <select className="input-lux" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="service-category-field">
                {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Short Description"><textarea rows={2} className="input-lux resize-none" value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} /></Field>
          <ImageUpload label="Thumbnail" value={form.thumbnail} onChange={(v) => setForm({ ...form, thumbnail: v })} testid="service-thumbnail" />
          <Field label="Full Description"><textarea rows={4} className="input-lux resize-none" value={form.full_description} onChange={(e) => setForm({ ...form, full_description: e.target.value })} /></Field>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Base Price (USDT)"><input type="number" className="input-lux" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
            <Field label="Delivery Time"><input className="input-lux" value={form.delivery_time} onChange={(e) => setForm({ ...form, delivery_time: e.target.value })} /></Field>
            <Field label="Sort Order"><input type="number" className="input-lux" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} /></Field>
          </div>
          <Field label="Features (one per line)"><textarea rows={3} className="input-lux resize-none" value={form.features.join("\n")} onChange={(e) => setFeatures(e.target.value)} /></Field>
          <div>
            <div className="flex items-center justify-between mb-2"><label className="label-lux">Packages</label>
              <button type="button" onClick={() => setForm({ ...form, packages: [...form.packages, { name: "", price: 0, description: "" }] })} className="text-xs text-amber-400" data-testid="add-package">+ Add package</button>
            </div>
            <div className="space-y-2">
              {form.packages.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <input placeholder="Name" className="input-lux col-span-3 !py-2 text-xs" value={p.name} onChange={(e) => updatePkg(i, "name", e.target.value)} />
                  <input placeholder="Price" type="number" className="input-lux col-span-2 !py-2 text-xs" value={p.price} onChange={(e) => updatePkg(i, "price", e.target.value)} />
                  <input placeholder="Description" className="input-lux col-span-6 !py-2 text-xs" value={p.description} onChange={(e) => updatePkg(i, "description", e.target.value)} />
                  <button type="button" onClick={() => setForm({ ...form, packages: form.packages.filter((_, j) => j !== i) })} className="col-span-1 text-red-400"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-amber-500" data-testid="service-published" /> Published</label>
            <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-amber-500" data-testid="service-featured" /> Featured</label>
          </div>
          <button disabled={saving} className="gold-btn w-full" data-testid="service-save-btn">{saving ? <Loader2 className="animate-spin" size={18} /> : "Save Service"}</button>
        </form>
      </Modal>
    </div>
  );
}
